/** Design tokens — palettes, typography, density (ported from DesignFiles) */

export type ThemeName = 'sunrise' | 'neon' | 'clover';

export interface ThemePalette {
  bg: string;
  surface: string;
  surfaceAlt: string;
  ink: string;
  inkSoft: string;
  inkMuted: string;
  line: string;
  accent: string;
  accentInk: string;
  accent2: string;
  accent3: string;
  lucky: string;
  win: string;
  gradient: string;
  confetti: string[];
}

export interface TypographyTokens {
  display: string;
  body: string;
  mono: string;
  displayWeight: number;
  displayLetter: string;
}

export type DensityName = 'compact' | 'comfy' | 'spacious';

export interface DensityTokens {
  pad: number;
  gap: number;
  rowH: number;
  numSize: number;
  numGap: number;
}

export const THEMES: Record<
  ThemeName,
  { name: string; light: ThemePalette; dark: ThemePalette }
> = {
  sunrise: {
    name: 'Sunrise',
    light: {
      bg: '#FFF6EC',
      surface: '#FFFFFF',
      surfaceAlt: '#FFEFD9',
      ink: '#1B1410',
      inkSoft: '#6B5A4E',
      inkMuted: '#A99585',
      line: 'rgba(27,20,16,0.08)',
      accent: '#FF6B3D',
      accentInk: '#FFFFFF',
      accent2: '#F5B82E',
      accent3: '#5B8FB9',
      lucky: '#E8375C',
      win: '#1F9D5F',
      gradient: 'linear-gradient(135deg, #FF6B3D 0%, #F5B82E 100%)',
      confetti: ['#FF6B3D', '#F5B82E', '#E8375C', '#5B8FB9', '#1F9D5F'],
    },
    dark: {
      bg: '#120C08',
      surface: '#1F1712',
      surfaceAlt: '#2A1F17',
      ink: '#FFF2E2',
      inkSoft: '#C9B39E',
      inkMuted: '#7D6A5B',
      line: 'rgba(255,242,226,0.08)',
      accent: '#FF7A4F',
      accentInk: '#120C08',
      accent2: '#FFC94A',
      accent3: '#7DB3DC',
      lucky: '#FF4F72',
      win: '#3BD185',
      gradient: 'linear-gradient(135deg, #FF7A4F 0%, #FFC94A 100%)',
      confetti: ['#FF7A4F', '#FFC94A', '#FF4F72', '#7DB3DC', '#3BD185'],
    },
  },
  neon: {
    name: 'Neon',
    light: {
      bg: '#F4F2EE',
      surface: '#FFFFFF',
      surfaceAlt: '#EAE6DE',
      ink: '#0B0B10',
      inkSoft: '#4A4A55',
      inkMuted: '#8A8A95',
      line: 'rgba(11,11,16,0.08)',
      accent: '#E6007A',
      accentInk: '#FFFFFF',
      accent2: '#C6F547',
      accent3: '#00C2D1',
      lucky: '#E6007A',
      win: '#00A86B',
      gradient: 'linear-gradient(135deg, #E6007A 0%, #C6F547 100%)',
      confetti: ['#E6007A', '#C6F547', '#00C2D1', '#FFD60A', '#FF6B3D'],
    },
    dark: {
      bg: '#08080C',
      surface: '#13131A',
      surfaceAlt: '#1C1C26',
      ink: '#F5F5FA',
      inkSoft: '#A5A5B3',
      inkMuted: '#606070',
      line: 'rgba(245,245,250,0.08)',
      accent: '#FF1F92',
      accentInk: '#08080C',
      accent2: '#D7FF4F',
      accent3: '#22E7FA',
      lucky: '#FF1F92',
      win: '#3BFF9E',
      gradient: 'linear-gradient(135deg, #FF1F92 0%, #D7FF4F 100%)',
      confetti: ['#FF1F92', '#D7FF4F', '#22E7FA', '#FFD60A', '#FF6B3D'],
    },
  },
  clover: {
    name: 'Clover',
    light: {
      bg: '#F5F1E6',
      surface: '#FFFDF5',
      surfaceAlt: '#EAE3CF',
      ink: '#0F1A13',
      inkSoft: '#45554A',
      inkMuted: '#8A9689',
      line: 'rgba(15,26,19,0.08)',
      accent: '#0E5B3A',
      accentInk: '#FFFDF5',
      accent2: '#C9A24B',
      accent3: '#8B3A3A',
      lucky: '#8B3A3A',
      win: '#0E5B3A',
      gradient: 'linear-gradient(135deg, #0E5B3A 0%, #1B7A4E 100%)',
      confetti: ['#0E5B3A', '#C9A24B', '#8B3A3A', '#4A2C20', '#F5F1E6'],
    },
    dark: {
      bg: '#0A0F0C',
      surface: '#121A15',
      surfaceAlt: '#1A241D',
      ink: '#F5F1E6',
      inkSoft: '#B8B39E',
      inkMuted: '#6B6A5B',
      line: 'rgba(245,241,230,0.08)',
      accent: '#3DB97A',
      accentInk: '#0A0F0C',
      accent2: '#E7C468',
      accent3: '#D85F5F',
      lucky: '#D85F5F',
      win: '#3DB97A',
      gradient: 'linear-gradient(135deg, #3DB97A 0%, #1B7A4E 100%)',
      confetti: ['#3DB97A', '#E7C468', '#D85F5F', '#F5F1E6', '#1B7A4E'],
    },
  },
};

export const TYPOGRAPHY: Record<ThemeName, TypographyTokens> = {
  sunrise: {
    display: '"Fraunces", "Times New Roman", serif',
    body: '"Inter", -apple-system, system-ui, sans-serif',
    mono: '"JetBrains Mono", "SF Mono", monospace',
    displayWeight: 500,
    displayLetter: '-0.02em',
  },
  neon: {
    display: '"Space Grotesk", -apple-system, system-ui, sans-serif',
    body: '"Space Grotesk", -apple-system, system-ui, sans-serif',
    mono: '"JetBrains Mono", "SF Mono", monospace',
    displayWeight: 700,
    displayLetter: '-0.03em',
  },
  clover: {
    display: '"Instrument Serif", "Times New Roman", serif',
    body: '"Geist", -apple-system, system-ui, sans-serif',
    mono: '"Geist Mono", "SF Mono", monospace',
    displayWeight: 400,
    displayLetter: '-0.01em',
  },
};

export const DENSITY: Record<DensityName, DensityTokens> = {
  compact: { pad: 14, gap: 10, rowH: 44, numSize: 30, numGap: 8 },
  comfy: { pad: 18, gap: 14, rowH: 52, numSize: 36, numGap: 10 },
  spacious: { pad: 22, gap: 20, rowH: 60, numSize: 44, numGap: 12 },
};

export function getPalette(theme: ThemeName, dark: boolean): ThemePalette {
  return THEMES[theme][dark ? 'dark' : 'light'];
}
